// tslint:disable:trailing-comma

import params from '../context/params';
import { paramTunnelAllParams } from '../bridge';
import { stat, ParamsTunnelBids } from '../stat';
import { retry, RetryStatus } from '../util/control-flow';
import { NeoNamespace } from '../common-enum';
import { NeoError } from '../util/neo-error';
import { isMock, getParamsTunnelData } from '../util/mock';
import { debug, DebugLevel } from '../util/debug';

export class ParamsTunnel {
  // 获取数据 Promise
  private fetchPromise?: Promise<any>;

  /**
   * 获取数据，调用获取全量参数桥 (neohybrid.useParamTunnel/getAllParams) 获取数据，失败或超时会发起最多三次重试，完成后对 params 进行赋值
   * @param {function} paramsValidator 参数校验函数
   * @returns {Promise} 获取数据 Promise
   */
  fetch<T>(paramsValidator?: (params: any) => boolean): Promise<T> {
    if (!this.fetchPromise) {
      const requestStartTime = new Date().getTime();
      let requestTime: number;
      stat.sendToLX(ParamsTunnelBids.FetchStart);
      stat.sendMetric('neo_param_tunnel_start');

      this.fetchPromise = new Promise((resolve, reject) => {
        this.getParamsWithRetry<T>(paramsValidator)
          .finally(() => {
            requestTime = new Date().getTime() - requestStartTime;
          })
          .then((data: T) => {
            stat.sendToLX(ParamsTunnelBids.FetchSuccess, {
              bridge_time: requestTime,
            });
            stat.sendMetric('neo_param_tunnel_succ', requestTime);
            params.setParams({ ...data });
            resolve(params.getParams());
          })
          .catch((err: NeoError | Error) => {
            stat.sendToLX(ParamsTunnelBids.FetchFail, {
              bridge_time: requestTime,
              error_message: err?.message,
            });
            stat.sendMetric('neo_param_tunnel_fail', requestTime, {
              error_message: err?.message,
            });
            reject(
              new NeoError(
                NeoNamespace.CoreParamsTunnel,
                'get params failed 3 times'
              )
            );
          });
      });
    }

    return this.fetchPromise;
  }

  /**
   * 重试调用获取参数数据，使用重试工具函数包装调用获取参数函数，失败或超时会发起最多三次重试
   * @param {function} paramsValidator 参数校验函数
   * @returns {Promise} 获取数据 Promise
   */
  private getParamsWithRetry<T>(
    paramsValidator?: (params: any) => boolean
  ): Promise<T> {
    return retry<T>((retryStatus: any) => this.getParams(retryStatus), {
      times: 3,
      interval: 200,
      validator: (data: any) => {
        if (paramsValidator) {
          return paramsValidator.call(null, data);
        }
        return true;
      },
    });
  }

  /**
   * 获取参数数据，调用获取全量参数桥 (neohybrid.useParamTunnel/getAllParams)
   * @param {RetryStatus} retryStatus 重试状态
   * @returns 桥调用 Promise
   */
  private getParams<T>(retryStatus?: RetryStatus): Promise<T> {
    let timeout: number = 0;
    const statParams: any = {};
    debug(NeoNamespace.UtilDebug, DebugLevel.Info, statParams);
    if (retryStatus) {
      const { times, delay } = retryStatus;

      statParams.retryTimes = times;
      statParams.retryDelay = delay;
      // 动态超时时间，注意超时时间 + retry delay 时间和 webview 强制推出时间（10s）对比
      switch (times) {
        // TP 50: 180+ iOS: 140 ~ 190 Android: 200 ~ 240
        // TP 95：800+ iOS: 480 ~ 640 Android: 800 ~ 850
        case 1:
          timeout = 1000;
          break;
        // TP 99：1300+ iOS: 1150 ~ 1540 Android: 1300 ~ 1330
        // TP 995：1800+ iOS: 1540 ~ 2050 Android: 1670 ~ 1730
        case 2:
          timeout = 2000;
          break;
        // TP 999：8200+ iOS: 8200 ~ 32800 Android: 4100 ~ 8120
        case 3:
          timeout = 8200;
          break;
        default:
      }
    }

    if (isMock()) {
      return Promise.resolve(getParamsTunnelData() as T);
    }

    return paramTunnelAllParams<T>({}, timeout, statParams);
  }
}

const paramsTunnel = new ParamsTunnel();

export { paramsTunnel };
export default paramsTunnel;
